import { useCompanySettings } from "hooks/useCompanySettings";
import { useMemo } from "react";
import type { IBrand } from "store/brandStore";
import type { AccountingFilter, AccountingMapping, TPortalType } from "store/types";
import { AccountingEntry, EPortalType } from "store/types";

export type AccountingTypologyFilter = {
    id: number;
    key: string | number;
    description: string;
};

export type RelatedResourceType = "brand" | "channel" | "relatedResource";

export type AccountingFilterOptions = Record<RelatedResourceType, AccountingTypologyFilter[] | null>;

/**
 * Hook to get the accounting typology filters
 * @param portalType - The portal type
 * @param brands - The brands
 * @param tabsValue - The tabs value
 * @returns The accounting typology filters
 */
export const useAccountingTypologyFilters = (
    portalType: TPortalType,
    brands: IBrand[],
    tabsValue: AccountingMapping["exportType"],
): { filters: AccountingTypologyFilter[]; filterOptions: AccountingFilterOptions } => {
    const { getCompanySettings } = useCompanySettings();
    const usesBrands = getCompanySettings("uses_multiple_brands");

    return useMemo(() => {
        switch (portalType) {
            case EPortalType.BOOK:
                return getBookAccountingTypologyFilters(usesBrands, brands, tabsValue);
            case EPortalType.PRESS:
            case EPortalType.AUDIOVISUAL:
                return getPressAccountingTypologyFilters();
            default:
                return { filters: [], filterOptions: { brand: null, channel: null, relatedResource: null } };
        }
    }, [brands, portalType, usesBrands, tabsValue]);
};

/**
 * Get the accounting typology filters for a book publisher
 * @param usesBrands - Whether the company uses multiple brands
 * @param brands - The brands
 * @param tabsValue - The tabs value
 * @returns The accounting typology filters
 */
const getBookAccountingTypologyFilters = (
    usesBrands: boolean,
    brands: IBrand[],
    tabsValue: AccountingMapping["exportType"],
) => {
    const filtersEditor: AccountingTypologyFilter[] = [
        { id: 1, key: "channel", description: "Accounting.Filters.Channel" },
        { id: 2, key: "relatedResource", description: "Accounting.Filters.RelatedResource" },
    ];

    // Related resources possibles on Book publisher accounts:
    const relatedResourceEditors: AccountingTypologyFilter[] = [
        { id: 1, key: "term", description: "Accounting.Filters.Contract" },
        { id: 2, key: "advance", description: "Accounting.Filters.Advance" },
        { id: 3, key: "advanceForfait", description: "Accounting.Filters.AdvanceForfait" },
    ];

    // All channel kinds possible:
    const channelKinds = [
        { id: 1, key: "physical", description: "NewChannel.Physical" },
        { id: 2, key: "additionalRights", description: "NewChannel.Numerical" },
        { id: 3, key: "collectiveManagement", description: "NewChannel.AuthorAssociation" },
        { id: 4, key: "audiovisualAdaptation", description: "NewChannel.RightTransfer" },
        { id: 4, key: "other", description: "NewChannel.Other" },
    ];

    const filterOptions: AccountingFilterOptions = {
        channel: channelKinds,
        relatedResource: relatedResourceEditors,
        brand: null,
    };

    if (tabsValue === "advanceReduction" || tabsValue === "allowance") {
        // fr : "diminution du minimum garanti", "provision"
        filterOptions.relatedResource = relatedResourceEditors.filter(
            (r) => !["advance", "advanceForfait"].includes(r.key as string),
        );
    }

    if (usesBrands && brands.length > 0) {
        filtersEditor.push({ id: 3, key: "brand", description: "Accounting.Filters.Brand" });
        filterOptions.brand = brands.map((brand) => ({
            id: brand.id!,
            key: brand.id!,
            description: brand.name,
        }));
    }

    return {
        filters: filtersEditor,
        filterOptions,
    };
};

/**
 * Get the accounting typology filters for a press publisher
 * @param usesBrands - Whether the company uses multiple brands
 * @param brands - The brands
 * @returns The accounting typology filters
 */
const getPressAccountingTypologyFilters = () => {
    // Filters that can be chosen on press accounts:
    const filtersPress: AccountingTypologyFilter[] = [
        // { key: "brand", description: "Marque" },
        // { key: "magazine", description: "Revue" },
        { id: 1, key: "relatedResource", description: "Accounting.Filters.RelatedResource" },
    ];

    // Related resources possibles on Press publisher accounts:
    const relatedResourcePress: AccountingTypologyFilter[] = [
        { id: 1, key: "article", description: "Accounting.Filters.Article" },
    ];

    return {
        filters: filtersPress,
        filterOptions: {
            brand: null,
            channel: null,
            relatedResource: relatedResourcePress,
        },
    };
};

/**
 * Detect which filter is applied on a given entry
 * @param entryFilters - The filters applied on the entry
 * @param filters - The filters available
 * @param filterOptions - The options available for each filter
 * @param usesFilters - Whether the company uses filters
 * @returns The applied filter key, its display name, and the chosen option name
 */
export const useAppliedFilter = (
    entryFilters: AccountingFilter,
    filters: AccountingTypologyFilter[],
    filterOptions: AccountingFilterOptions,
    usesFilters: boolean,
): { appliedFilter: string; filterName: string; filterOptionName: string } => {
    return useMemo(() => {
        if (!usesFilters) return { appliedFilter: "", filterName: "", filterOptionName: "" };

        const entryFilterRecord = entryFilters as Record<string, unknown>;

        const filterKey =
            Object.keys(entryFilterRecord).find((key) => key !== "typology" && entryFilterRecord[key] !== "all") ?? "";

        const name = filters.find((f) => f.key === filterKey)?.description ?? "";

        const options = filterKey ? filterOptions[filterKey as RelatedResourceType] : null;
        const chosenValue = filterKey ? entryFilterRecord[filterKey] : "";
        const optionName = options?.find((o) => o.key === chosenValue)?.description ?? "";

        return { appliedFilter: filterKey, filterName: name, filterOptionName: optionName };
    }, [usesFilters, entryFilters, filters, filterOptions]);
};
